import React, { useCallback, useEffect, useState } from 'react'
import { View, Text as RNText, TextStyle, Pressable } from 'react-native'
import { observer } from 'mobx-react-lite'
import { DateTime } from 'luxon'
import { Box } from '@mui/material'
import { Text } from '@trivie/ui-web/components/text'

import { useStores, FilterTermsProps } from '@trivie/core'
import { ResourceRow } from '@trivie/ui-web/components/resource-row'
import { color } from '@trivie/ui-mobile'
import { Dialog } from '@trivie/ui-web/components/dialogs/dialog'
import { isVimeo, isYoutube } from '@trivie/core/src/utils/mimetype'
import { useHistory } from 'react-router-dom'

export interface ResourcesTabProps {
  searchTerm?: string
  filterTerms?: FilterTermsProps
  clearFilters: () => void
}

export const ResourcesTab = observer((props: ResourcesTabProps) => {
  const { libraryStore, userStore, resourceStore } = useStores()
  const { allResources } = libraryStore
  const { markAsViewed, setEndViewed, setStartViewed, getSourceDetails, resourceDetails } =
    resourceStore
  const { enterpriseTier } = userStore
  const { clearFilters, searchTerm, filterTerms } = props

  const [viewingTextSource, setViewingTextSource] = useState<any>()

  const onFocus = () => {
    const resourceData = localStorage.getItem('viewing-resource')
    if (resourceData) {
      const resource = JSON.parse(resourceData)
      if (resourceData) {
        localStorage.removeItem('viewing-resource')
        setEndViewed(DateTime.now().toUTC().toISO())
        markAsViewed(resource.resource_id, resource.metadata)
      }
    }
  }

  const history = useHistory()
  useEffect(() => {
    window.addEventListener('focus', onFocus)
    onFocus()
    return () => window.removeEventListener('focus', onFocus)
  }, [])

  const viewResource = (resource) => {
    setStartViewed(DateTime.now().toUTC().toISO())
    getSourceDetails(resource.source_id).then((data) => {
      if (data) {
        if (data?.raw_text) {
          setViewingTextSource({ ...data, metadata: resource.metadata })
        } else if (data?.isVideo || data?.isAudio) {
          localStorage.setItem(
            'viewing-resource',
            JSON.stringify({ resource_id: data.resource_id, metadata: resource.metadata }),
          )
          history.push(
            `/library/source/?source_id=${resource.source_id}&assignment_id=${resource.metadata.assignment_id}&content_user_id=${resource.metadata.content_user_id}&path_id=${resource.metadata.path_id}&quiz_id=${resource.metadata.quiz_id}&topic_id=${resource.metadata.topic_id}`,
          )
        } else {
          localStorage.setItem(
            'viewing-resource',
            JSON.stringify({ resource_id: data.resource_id, metadata: resource.metadata }),
          )
          window.open(
            // @ts-ignore
            data.converted_pdf_url ? data.converted_pdf_url : data.filename_or_url,
            '_blank',
            'noopener',
          )
        }
      }
    })
  }

  const getData = useCallback(() => {
    const fullData = allResources
      .slice()
      ?.filter(
        (f) => !searchTerm || f.title.toLocaleLowerCase().includes(searchTerm.toLocaleLowerCase()),
      )

    if (!filterTerms?.sources || filterTerms.sources.length === 0) return fullData

    let result: typeof fullData = []

    //gather urls
    if (filterTerms.sources.includes('url')) result = fullData.filter((f) => f.url)

    const filterItems: string[] = []
    filterTerms?.sources.map((e) => {
      if (Array.isArray(e)) {
        e.forEach((element) => {
          filterItems.push(element)
        })
      } else {
        filterItems.push(e)
      }
    })

    result = fullData.filter((f) => !f.extension || filterItems.includes(f.extension))
    return result
  }, [allResources, filterTerms, searchTerm])

  const filteredData = getData()

  return (
    <div style={{ marginTop: 4 }}>
      {filteredData.map((r, idx) => (
        <ResourceRow
          key={`${r.title}_${idx}`}
          enterpriseTier={enterpriseTier}
          resource={r}
          style={{ marginBottom: 16 }}
          onPress={() => viewResource(r)}
        />
      ))}
      <Dialog
        maxWidth="sm"
        align="left"
        onClose={() => {
          setViewingTextSource(null)
          setEndViewed(DateTime.now().toUTC().toISO())
          markAsViewed(viewingTextSource?.resource_id, viewingTextSource?.metadata)
        }}
        title="Preview"
        open={Boolean(viewingTextSource)}
      >
        <Box flexDirection="column" display="flex" p={2} sx={{ whiteSpace: 'pre-wrap' }}>
          <Text mb={2} variant="headline" text={viewingTextSource?.title} />
          {viewingTextSource?.raw_text}
        </Box>
      </Dialog>
      {filteredData?.length !== allResources.length ? (
        <View style={{ paddingHorizontal: 40, paddingTop: filteredData?.length === 0 ? 120 : 12 }}>
          <RNText style={FILTER}>
            {`${
              allResources.length - filteredData?.length
            } other sources are not shown due to your active filters.`}
            <Pressable onPress={() => clearFilters()}>
              <RNText style={{ ...FILTER, ...CLEAR }}>{'Clear Filters?'}</RNText>
            </Pressable>
          </RNText>
        </View>
      ) : null}
    </div>
  )
})

const FILTER: TextStyle = {
  alignSelf: 'center',
  textAlign: 'center',
  fontStyle: 'italic',
  color: color.shade50,
}
const CLEAR: TextStyle = {
  alignSelf: 'center',
  paddingTop: 40,
  paddingLeft: 4,
  textDecorationLine: 'underline',
}
